/* ========================================================================
   Build & Delivery‑Time Mapper – MULTILINGUAL EDITION
   ------------------------------------------------------------------------
   • Drop this file into  assets/build‑delivery‑mapper.js  (or similar) and
     include it in theme.liquid with:
       {{ 'build-delivery-mapper.js' | asset_url | script_tag }}
   • Requires the following globals (already present in your theme):
       – buildTimeData   (array of build‑time objects)
       – deliveryTimes   (object with { uk, us, eu, fr, ch, at, display_text })
       – product_js      (Shopify product JSON)
       – market_handle   (current market handle, e.g. 'uk', 'fr' …)
       – getSelectedOptions() ⇒ [{ value : 'string', … }, …]
   • Extending to new languages = edit the TRANSLATIONS object only.
   =======================================================================*/

/* -----------------------------------------------------------------------
   0 · TRANSLATIONS
   --------------------------------------------------------------------- */
const TRANSLATIONS = {
  /* ---------- English ---------- */
  en: {
    set:       ['set'],
    stock:     ['stock'],
    noTable:   ['without table', 'no table'],
    noBench:   ['without bench', 'no bench'],
    wood: {
      walnut:  ['walnut'],
      oak:     ['oak'],
      ash:     ['ash']
    },
    tableWord: 'table',
    benchWord: 'bench'
  },

  /* ---------- German ---------- */
  eu: {
    set:       ['kollektion', 'satz'],
    stock:     ['lagerbestand', 'lager'],
    noTable:   ['ohne tisch', 'kein tisch'],
    noBench:   ['ohne bank', 'kein bank'],
    wood: {
      walnut:  ['nussbaum'],
      oak:     ['eiche'],
      ash:     ['esche']
    },
    tableWord: 'tisch',
    benchWord: 'bank'
  },

  /* ---------- French ---------- */
  fr: {
    set:       ['collection'],
    stock:     ['stock'],
    noTable:   ['sans table'],
    noBench:   ['sans banc'],
    wood: {
      walnut:  ['noyer'],
      oak:     ['chêne'],
      ash:     ['fumé']
    },
    tableWord: 'table',
    benchWord: 'banc'
  }
};

/* -----------------------------------------------------------------------
   1 · MARKET → LANGUAGE mapping
   --------------------------------------------------------------------- */
const REGION_TO_LANG = {
  gb: 'en', uk: 'en', us: 'en',
  eu: 'eu', at: 'eu', ch: 'eu', swiss: 'eu',
  fr: 'fr'
  
};

const MARKET_TO_REGION = {
  gb: 'uk', us: 'us',
  eu: 'eu', at: 'at', ch: 'ch', swiss: 'ch',
  fr: 'fr'
  
};







/* -----------------------------------------------------------
   filterCandidatesByProductKind
   -----------------------------------------------------------
   • If the product title (any language) contains a table word,
     keep only rows whose tags also contain a table synonym.
   • Same for bench. If the title has neither word, the function
     returns the list untouched.
----------------------------------------------------------- */
function filterCandidatesByProductKind(candidates, title) {
  /* single cached arrays of every translated word */
  const TABLE_WORDS = window._TABLE_WORDS_ ||
    (window._TABLE_WORDS_ = Array.from(
      new Set(Object.values(TRANSLATIONS).map(l => l.tableWord.toLowerCase()))
    ));

  const BENCH_WORDS = window._BENCH_WORDS_ ||
    (window._BENCH_WORDS_ = Array.from(
      new Set(Object.values(TRANSLATIONS).map(l => l.benchWord.toLowerCase()))
    ));

  const isTableProduct = TABLE_WORDS.some(w => title.includes(w));
  const isBenchProduct = BENCH_WORDS.some(w => title.includes(w));

  /* nothing to do → give back original list */
  if (!isTableProduct && !isBenchProduct) return candidates;

  return candidates.filter(row => {
    const tagSet = row.tags.map(t => String(t).toLowerCase());
    if (isTableProduct) return tagSet.some(t => TABLE_WORDS.includes(t));
    if (isBenchProduct) return tagSet.some(t => BENCH_WORDS.includes(t));
    return true;                       // fallback (shouldn’t run)
  });
}








/* -----------------------------------------------------------------------
   2 · Helper: week‑number formatter
   --------------------------------------------------------------------- */
function formatWeeks(n) {
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  const lo  = Math.floor(num);
  return (num - lo) >= 0.5 ? `${lo}-${lo + 1}` : String(lo);
}

/* -----------------------------------------------------------------------
   3 · MAIN: mapProductToBuildData()
   --------------------------------------------------------------------- */
function mapProductToBuildData() {
  /* ---------- guards ---------- */
  if (typeof buildTimeData === 'undefined' ||
      typeof deliveryTimes  === 'undefined'||
      typeof product_js     === 'undefined'||
      typeof getSelectedOptions !== 'function') {
    console.error('mapProductToBuildData(): required globals missing');
    return null;
  }

  /* ---------- context ---------- */
  const region   = (market_handle ?? 'uk').toLowerCase();
  const langKey  = REGION_TO_LANG[region] || 'en';
  const T        = TRANSLATIONS[langKey]  || TRANSLATIONS.en;
  const regKey   = MARKET_TO_REGION[region] || region;   // gb→uk etc.

  const titleRaw = product_js.title ?? '';
  const title    = titleRaw.toLowerCase();

  const optionValues = (getSelectedOptions() || [])
                       .map(o => (o.value ?? '').toLowerCase().trim());

  /* ---------- early‑exit for stock ---------- */
  if (T.stock.some(w => title.includes(w))) return null;

  /* ---------- wood bucket ---------- */
  const woodType = T.wood.walnut.some(w => title.includes(w))
                   ? 'walnut'
                   : 'oak_ash';

  let candidates = buildTimeData.filter(row => {
    const cat = (row.category ?? '').toLowerCase();
    return woodType === 'walnut'
      ? cat.includes('walnut')
      : cat.includes('oak') || cat.includes('ash');
  });

  /* NEW: narrow by table / bench if those words are in the title */
    candidates = filterCandidatesByProductKind(candidates, title);
  
  if (!candidates.length) return null;

  /* ---------- set / option analysis ---------- */
  const isSet       = T.set.some(w => title.includes(w));
  const optsNoTable = optionValues.some(v => T.noTable.some(p => v.includes(p)));
  const optsNoBench = optionValues.some(v => T.noBench.some(p => v.includes(p)));

  /* ---------- FILTER candidates when table / bench excluded ---------- */
  if (optsNoTable) {
    const tableWords = Object.values(TRANSLATIONS)
      .map(l => l.tableWord.toLowerCase());
    candidates = candidates.filter(row =>
      !row.tags.some(tag => tableWords.includes(String(tag).toLowerCase()))
    );
  }
  if (optsNoBench) {
    const benchWords = Object.values(TRANSLATIONS)
      .map(l => l.benchWord.toLowerCase());
    candidates = candidates.filter(row =>
      !row.tags.some(tag => benchWords.includes(String(tag).toLowerCase()))
    );
  }
  if (!candidates.length) return null;

  /* ---------- clean options & extras ---------- */
  const stripWords = (s, words) =>
    words.reduce((x, w) => x.replace(new RegExp(`\\b${w}\\b`, 'gi'), ''), s)
         .replace(/\s{2,}/g, ' ').trim();

  let cleanOpts = optionValues.slice();
  if (optsNoTable) cleanOpts = cleanOpts.map(v => stripWords(v, [T.tableWord]));
  if (optsNoBench) cleanOpts = cleanOpts.map(v => stripWords(v, [T.benchWord]));

  const extras = [];
  if (isSet && !optsNoTable) extras.push(T.tableWord);
  if (isSet && !optsNoBench) extras.push(T.benchWord);
  const isStorage = (product_js.type || '').toLowerCase() == 'storage' || (product_js.type || '').toLowerCase() == 'rangement' || (product_js.type || '').toLowerCase() == 'rangement';

  let storage = '';
  if (isStorage)
    storage = (product_js.type || '').toLowerCase();
  
  const matchPool = isSet
    ? [...cleanOpts, ...extras, title]
    : [title, ...cleanOpts, storage];
debugger;
  /* ---------- tag‑scoring helper ---------- */
  const scoreTags = tags =>
    tags.reduce((c, t) =>
      matchPool.some(s => s.includes(String(t).toLowerCase())) ? c + 1 : c, 0);

  /* ---------- choose best candidate ---------- */
  let best = null, bestHits = -1;
  for (const row of candidates) {
    const hits = scoreTags(row.tags);
    if (hits > bestHits ||
        (hits === bestHits && best &&
         (row[`build_${regKey}`] ?? 0) > best[`build_${regKey}`])) {
      best = row;
      bestHits = hits;
    }
  }
  if (!best || bestHits === 0) return null;

  /* ---------- assemble payload ---------- */
  const buildRaw = Number(best[`build_${regKey}`]);
  const shipRaw  = Number(deliveryTimes[regKey]);
  if (Number.isNaN(buildRaw) || Number.isNaN(shipRaw)) return null;

  const build = formatWeeks(buildRaw);
  const ship  = formatWeeks(shipRaw);

  const RE_B = new RegExp('{' + '{\\s*b_time\\s*}' + '}', 'gi');
  const RE_D = new RegExp('{' + '{\\s*d_time\\s*}' + '}', 'gi');

  const display = (deliveryTimes.display_text ?? '')
                    .replace(RE_B, build)
                    .replace(RE_D, ship);

  return {
    buildTime:    build,
    deliveryTime: ship,
    displayText:  display,
    matchedTags:  best.tags,
    matchedObjectHandle: best.handle
  };
}















/* ============================================================
   Delivery‑banner updater – DE / EN plural + bold labels
   ============================================================ */

let originalDeliveryHTML = null;
const DELIVERY_SELECTOR  = '.delivery_time';

/* --- decode &lt; / &gt; etc. ---------------------------------- */
const decodeHTML = str => {
  const t = document.createElement('textarea');
  t.innerHTML = str;
  return t.value;
};

/* --- singular ↔ plural helper ------------------------------ */
function pluraliseWeeks(text, buildStr, delStr) {
  let out = text;
  const swap = (num, plural, singular) => {
    if (num === '1') {
      out = out.replace(new RegExp(`${num}\\s+${plural}`, 'i'),
                        `${num} ${singular}`);
    }
  };
  if (/wochen/i.test(out)) { swap(buildStr, 'WOCHEN', 'WOCHE'); swap(delStr,'WOCHEN','WOCHE'); }
  if (/weeks/i.test(out))  { swap(buildStr, 'WEEKS',  'WEEK');  swap(delStr, 'WEEKS', 'WEEK'); }
  return out;
}

/* --- main refresh ----------------------------------------- */
function update_build_delivery_text() {
  const el = document.querySelector(DELIVERY_SELECTOR);
  if (!el) return;

  if (originalDeliveryHTML === null) originalDeliveryHTML = el.innerHTML;

  const res = typeof mapProductToBuildData === 'function' ? mapProductToBuildData() : null;

  let html = originalDeliveryHTML;

  if (res && res.displayText) {
    let txt = decodeHTML(res.displayText);                   // un‑escape tags
    txt     = pluraliseWeeks(txt, res.buildTime, res.deliveryTime);
    html    = `<p class="delivery-banner-text">${txt}</p>`;
  }

  if (el.innerHTML !== html) el.innerHTML = html;
}

/* run once */
// if (document.readyState === 'loading') {
//   document.addEventListener('DOMContentLoaded', update_build_delivery_text);
// } else {
//   update_build_delivery_text();
// }

/* expose */
window.update_build_delivery_text = update_build_delivery_text;
